"use client";

import React from "react";
import { Phone, Mail, MapPin, Clock, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const CONTACT_INFO = {
  phone: "+1 (800) CIRCUIT-X",
  email: "concierge@circuitxtravel.com",
  address: "77 Luxury Row, West Hollywood, CA 90069",
  hours: "Available 24/7 for Premium Members"
};
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-medium text-foreground tracking-tight">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md font-sans">Thank you for contacting CircuitXtravel. We've received your inquiry and will respond within 24 business hours.</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-md border-primary/30 font-sans uppercase tracking-widest text-xs">
          Send Another Message
        </Button>
      </div>;
  }
  const inputBaseClasses = "flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 font-sans";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Please provide your full name."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground">Full Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input type="text" className={`${inputBaseClasses} h-10`} placeholder="Alexander Sterling" {...field} />
              </FormControl>
              <FormMessage className="text-[10px]" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Email is required for our concierge to reach you.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground">Email Address <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <input type="email" className={`${inputBaseClasses} h-10`} placeholder="alex@luxury.com" {...field} />
              </FormControl>
              <FormMessage className="text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <input type="tel" className={`${inputBaseClasses} h-10`} placeholder="+1 (555) 000-0000" {...field} />
              </FormControl>
              <FormMessage className="text-[10px]" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-6">
        <FormField control={control} name="interest" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground">Inquiry Type</FormLabel>
              <FormControl>
                <select className={`${inputBaseClasses} h-10 appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22currentColor%22%20stroke-width%3D%221%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:1em_1em] bg-[right_0.75rem_center] bg-no-repeat pr-10`} {...field}>
                  <option value="" disabled>Select Category</option>
                  {["General Inquiry", "Partnerships & Marketing", "Media & PR", "Careers"].map((option, i) => <option key={i} value={option} data-index={i}>{option}</option>)}
                </select>
              </FormControl>
              <FormMessage className="text-[10px]" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please share your message with us."
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground">Your Message <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <textarea className={`${inputBaseClasses} min-h-[120px] resize-none`} placeholder="How can we assist you today?" {...field} />
            </FormControl>
            <FormDescription className="text-[10px] italic">Our team is here to help with travel planning, booking inquiries, and general questions.</FormDescription>
            <FormMessage className="text-[10px]" />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-md bg-destructive/5 border border-destructive/10 flex items-start gap-3 text-destructive">
          <CircleAlert className="size-4 shrink-0 mt-0.5" />
          <p className="text-xs font-sans">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[240px] bg-foreground text-background hover:bg-foreground/90 rounded-md font-sans uppercase tracking-widest text-xs h-12 transition-all duration-500" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Processing...
          </> : 'Send Inquiry'}
      </Button>
    </Form>;
}
export default function ConnectWithAnAgent() {
  return <section data-section-id="234628" id="connect-with-an-agent" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-primary)_0%,transparent_25%)] opacity-5 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {}
          <div className="lg:col-span-5 flex flex-col space-y-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-default font-light text-foreground tracking-tight mb-6">
                Connect with <br /><span className="italic">an Agent</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-sans font-light max-w-md">
                Experience the pinnacle of personalized travel. Our boutique concierge service is dedicated to crafting seamless, high-octane celebrations and ultra-luxury escapes for the premium LGBTQ+ community.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-6">
                <div className="size-12 rounded-md bg-secondary flex items-center justify-center shrink-0 border border-border/50">
                  <Phone className="size-5 text-foreground/70" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground">Direct Line · Call or WhatsApp</h4>
                  <div className="flex items-center gap-2.5 mt-1 flex-wrap">
                    <a href="tel:+17789174568" className="text-foreground hover:text-primary transition-colors font-default text-lg">
                      +1 778 917 4568
                    </a>
                    <Link href="https://wa.me/17789174568" newTab aria-label="Chat on WhatsApp" className="inline-flex items-center justify-center size-7 rounded bg-secondary/80 hover:bg-secondary text-foreground/70 hover:text-foreground border border-border/50 transition-colors">
                      <svg className="size-4 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm.01 1.67c2.2 0 4.26.86 5.82 2.42a8.23 8.23 0 0 1 2.41 5.82c0 4.54-3.7 8.24-8.24 8.24-1.42 0-2.81-.37-4.04-1.07l-.29-.17-3.12.82.83-3.04-.19-.3a8.21 8.21 0 0 1-1.26-4.46c0-4.54 3.7-8.24 8.24-8.24zm-3.53 4.2c-.19 0-.5.07-.76.36-.26.29-1 .98-1 2.39s1.02 2.77 1.16 2.96c.14.19 1.97 3.16 4.88 4.31.69.27 1.23.44 1.66.57.7.22 1.34.19 1.84.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35-.29-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.29-.77.96-.94 1.16-.17.19-.35.22-.64.07-.29-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.29-.02-.45.13-.59.13-.13.29-.35.44-.52.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.56-.01z" />
                      </svg>
                    </Link>
                  </div>
                  <p className="text-xs text-muted-foreground/60 mt-1 font-sans">{CONTACT_INFO.hours}</p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="size-12 rounded-md bg-secondary flex items-center justify-center shrink-0 border border-border/50">
                  <svg className="size-5 fill-current text-foreground/70" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground">Instagram</h4>
                  <Link href="https://www.instagram.com/circuitxtravel" newTab className="block text-foreground hover:text-primary transition-colors mt-1 font-default text-lg truncate">
                    @circuitxtravel
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="size-12 rounded-md bg-secondary flex items-center justify-center shrink-0 border border-border/50">
                  <Mail className="size-5 text-foreground/70" />
                </div>
                <div>
                  <h4 className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground">Inquiries</h4>
                  <p className="text-foreground mt-1 font-default text-lg">info@circuitxtravel.com</p>
                </div>
              </div>

              
            </div>

            <div className="pt-8 border-t border-border/40">
              <Card className="bg-secondary/30 backdrop-blur-md border-border/50 shadow-sm rounded-md overflow-hidden">
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-default font-medium flex items-center gap-3">
                    <Clock className="size-5 text-primary" />
                    Response Time
                  </CardTitle>
                  <CardDescription className="font-sans text-sm">
                    Our dedicated support team monitors all general inquiries around the clock. You can expect a detailed response within 24 business hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground font-sans italic">
                    For urgent travel assistance, please use our direct line above.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="h-full shadow-xl border-border/30 bg-card/40 backdrop-blur-xl rounded-md">
              <CardHeader className="pb-8">
                <CardTitle className="text-2xl font-default font-medium tracking-tight">Send Us a Message</CardTitle>
                <CardDescription className="font-sans">
                  Have a question about our services, partnerships, or media? Fill out the form below and our team will get back to you shortly.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formKey="contact_general_form" formId="contact_general_form" sectionName="Connect with an Agent" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                interest: "",
                message: ""
              }} submitText="Send Inquiry" successMessage="Thank you for contacting CircuitXtravel. We've received your inquiry and will respond within 24 business hours.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}